import { createFileRoute, useNavigate, redirect } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { LogIn } from "lucide-react";
import { api, ApiError } from "@/lib/api";

export const Route = createFileRoute("/login")({
  component: LoginPage,
  beforeLoad: async () => {
    // Si ya hay sesión, mandamos al dashboard.
    try {
      await api.get("/auth/me");
      throw redirect({ to: "/" });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return; // ok, login fresh
      // Para cualquier otro error (network, etc.) dejamos entrar al login.
    }
  },
});

function LoginPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const login = useMutation({
    mutationFn: () => api.post("/auth/login", { email, password }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["auth", "me"] });
      navigate({ to: "/" });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 401) {
        setError("Email o contraseña incorrectos.");
      } else {
        setError("Error al conectar. Intentá de nuevo.");
      }
    },
  });

  return (
    <div className="min-h-screen grid place-items-center bg-base-200 p-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          login.mutate();
        }}
        className="card-elevated w-full max-w-sm p-6 space-y-4"
      >
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
            Axorial
          </div>
          <h1 className="text-2xl font-display font-semibold mt-1">Portal de Clientes</h1>
          <p className="text-sm text-base-content/65 mt-1">
            Ingresá con el email y contraseña que te compartió tu contacto en
            Axorial.
          </p>
        </div>
        <label className="form-control w-full">
          <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">
            Email
          </span>
          <input
            type="email"
            className="input input-bordered w-full"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            autoFocus
            required
          />
        </label>
        <label className="form-control w-full">
          <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">
            Contraseña
          </span>
          <input
            type="password"
            className="input input-bordered w-full"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </label>
        {error && (
          <div className="text-sm text-error border border-error/30 bg-error/5 rounded-md p-2">
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={login.isPending || !email || !password}
          className="btn btn-primary w-full"
        >
          {login.isPending && <span className="loading loading-spinner loading-xs" />}
          <LogIn className="size-4" />
          Entrar
        </button>
        <div className="text-xs text-base-content/55 text-center pt-1">
          ¿Olvidaste tu contraseña? Contactá a tu lead en Axorial — te van a
          generar un link para resetearla.
        </div>
      </form>
    </div>
  );
}
